import React, { createContext, useContext, useMemo } from "react";
import { chartConfig } from "./chartConfig"; // adjust path as needed

// ─────────────────────────────────────────────────────────────────────────────
// Public types (unchanged — consumers don't need to change)
// ─────────────────────────────────────────────────────────────────────────────

export interface ScaledTypography {
	title: number;
	axisLabel: number;
	axisLabelTbl: number;
	yAxisLabel: number;
	dataLabel: number;
	dataLabelTbl: number;
	legend: number;
	nSize: number;
	nSizeTbl: number;
	subtitle: number;
}

export interface ScaledGeometry {
	maxBarWidth: number;
	maxVStackedBarWidth: number;
	strokeWidth: number;
	markerRadius: number;
	gridTop: number;
	cellPaddingV: number;
	cellPaddingH: number;
	legendSpacing: number;
	axisColumnWidth: number;
}

export interface ScaledLayout {
	headerRowHeight: number;
	nSizeRowHeight: number;
	maxAttrLabelLines: number;
	legendLines: number;
	containerPadding: number;
	showAxisLabels: boolean;
	showSubtitle: boolean;
	showFootnote: boolean;
	showLegend: boolean;
	showViewToggle: boolean;
}

export interface ScaledConfig {
	scaleFactor: number;
	isTooSmall: boolean;
	tilesInRow: number; // ← NEW
	typography: ScaledTypography;
	geometry: ScaledGeometry;
	layout: ScaledLayout;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core computation — now purely config-driven, zero magic numbers
// ─────────────────────────────────────────────────────────────────────────────

export function computeScaledConfig(tileWidth: number, tileHeight: number, scaleFactor: number): ScaledConfig {
	// Pull everything from the single source of truth
	const cfg = chartConfig.getMultiTileScalingConfig();
	const { floors, visibilityThresholds: vt, steppedThresholds: st, base } = cfg;

	const isTooSmall = tileWidth < cfg.minTileSize.width || tileHeight < cfg.minTileSize.height;

	/**
	 * Scale a base value and clamp to a hard floor.
	 * Rounds to 1 decimal so values stay clean (e.g. 9.0, not 8.999999).
	 */
	const s = (baseVal: number, floor: number): number => {
		const result = Math.max(floor, Math.round(baseVal * scaleFactor * 10) / 10);
		return result;
	};

	const typography: ScaledTypography = {
		title: s(base.typography.title, floors.titlePx),
		axisLabel: s(base.typography.axisLabel, floors.fontPx),
		axisLabelTbl: s(base.typography.axisLabelTbl, floors.yAxisLabelFontPx),
		yAxisLabel: s(base.typography.yAxisLabel, floors.yAxisLabelFontPx),
		dataLabel: s(base.typography.dataLabel, floors.labelFontPx),
		dataLabelTbl: s(base.typography.dataLabelTbl, floors.labelFontPxTbl),
		legend: s(base.typography.legend, floors.legendFontPx),
		nSize: s(base.typography.nSize, floors.fontPx),
		nSizeTbl: s(base.typography.nSize, floors.labelFontPxTbl),
		subtitle: s(base.typography.subtitle, floors.fontPx),
	};

	const geometry: ScaledGeometry = {
		maxBarWidth: s(base.geometry.maxBarWidth, floors.barPx),
		maxVStackedBarWidth: s(base.geometry.maxVStackedBarWidth, floors.vStackedBarPx),
		strokeWidth: s(base.geometry.strokeWidth, floors.strokePx),
		markerRadius: s(base.geometry.markerRadius, 1),
		gridTop: s(base.geometry.gridTop, 4),
		cellPaddingV: s(base.geometry.cellPaddingV, 1),
		cellPaddingH: s(base.geometry.cellPaddingH, 1),
		legendSpacing: s(base.geometry.legendSpacing, 4),
		axisColumnWidth: s(base.geometry.axisColumnWidth, floors.axisColumnPx),
	};

	const layout: ScaledLayout = {
		headerRowHeight: Math.max(floors.headerRowHeight, Math.round(base.layout.headerRowHeight * scaleFactor)),
		nSizeRowHeight: Math.max(floors.nSizeRowHeight, Math.round(base.layout.nSizeRowHeight * scaleFactor)),
		containerPadding: Math.max(floors.containerPadding, Math.round(base.layout.containerPadding * scaleFactor)),
		maxAttrLabelLines: scaleFactor > st.attrLabelLines.high ? base.layout.maxAttrLabelLines : scaleFactor > st.attrLabelLines.low ? 2 : 2,
		legendLines: scaleFactor > st.legendLines.threshold ? base.layout.legendLines : 1,
		showSubtitle: scaleFactor > vt.subtitle,
		showAxisLabels: scaleFactor > vt.axisLabels,
		showViewToggle: scaleFactor > vt.viewToggle,
		showLegend: scaleFactor > vt.legend,
		showFootnote: false,
	};

	return { scaleFactor, isTooSmall, tilesInRow: 1, typography, geometry, layout };
}

// ─────────────────────────────────────────────────────────────────────────────
// IDENTITY_SCALE — single-chart fallback, computed once at module load
//
// Because computeScaledConfig(scaleFactor=1) maps every s(base, floor) back
// to exactly `base`, and base values mirror chartConfig exactly, single-chart
// output is byte-identical to the old code.
// ─────────────────────────────────────────────────────────────────────────────

const buildIdentityScale = (): ScaledConfig => {
	const cfg = chartConfig.getMultiTileScalingConfig();
	// Pass dummy tile dimensions well above minTileSize so isTooSmall = false
	return computeScaledConfig(cfg.reference.width, cfg.reference.height, 1.0);
};

// Stable module-level reference — never re-created, never triggers useMemo
const IDENTITY_SCALE: ScaledConfig = buildIdentityScale();

// ─────────────────────────────────────────────────────────────────────────────
// Context + hook
// ─────────────────────────────────────────────────────────────────────────────

const ChartScaleContext = createContext<ScaledConfig | null>(null);

export function useScaledConfig(): ScaledConfig {
	return useContext(ChartScaleContext) ?? IDENTITY_SCALE;
}

// ─────────────────────────────────────────────────────────────────────────────
// ChartContainer — self-consuming, no scaledPadding prop needed anywhere
// ─────────────────────────────────────────────────────────────────────────────

interface ChartContainerProps {
	children: React.ReactNode;
	className?: string;
	innerRef?: React.RefObject<HTMLDivElement>;
}

export const ChartContainer: React.FC<ChartContainerProps> = ({ children, className = "", innerRef }) => {
	const scaled = useScaledConfig();
	const basePadding = chartConfig.getMultiTileScalingConfig().base.layout.containerPadding;

	// Only override with inline style when the value deviates from the Tailwind
	// baseline (p-4 = 16px). In single-chart mode scaled.layout.containerPadding
	// === basePadding === 16, so paddingStyle stays {}, preserving the exact
	// same CSS specificity path as the old component.
	const paddingStyle = scaled.layout.containerPadding !== basePadding ? { padding: `${scaled.layout.containerPadding}px` } : {};

	return (
		<div ref={innerRef} className={`bg-white p-2 w-full h-full flex flex-col font-sans ${className}`} style={paddingStyle}>
			{children}
		</div>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// ChartScaleProvider — wraps each tile cell in MultiTileGrid
// ─────────────────────────────────────────────────────────────────────────────

interface ChartScaleProviderProps {
	children: React.ReactNode;
	wrapperWidth: number;
	wrapperHeight: number;
	tileWidth: number;
	tileHeight: number;
}

// ChartScaleContext.tsx
export const ChartScaleProvider: React.FC<ChartScaleProviderProps> = ({ children, wrapperWidth, wrapperHeight, tileWidth, tileHeight }) => {
	const scaled = useMemo(() => {
		// Wait until all four dimensions are real positive numbers
		if (!wrapperWidth || !wrapperHeight || !tileWidth || !tileHeight || wrapperWidth < 1 || wrapperHeight < 1 || tileWidth < 1 || tileHeight < 1) {
			// Return null so the tile renders nothing until dimensions resolve
			return null;
		}

		const scaleW = tileWidth / wrapperWidth;
		const scaleH = tileHeight / wrapperHeight;
		const scaleFactor = Math.min(1, Math.sqrt(scaleW * scaleH));
		const tilesInRow = Math.max(1, Math.round(wrapperWidth / tileWidth));

		return {
			...computeScaledConfig(tileWidth, tileHeight, scaleFactor),
			tilesInRow,
		};
	}, [wrapperWidth, wrapperHeight, tileWidth, tileHeight]);

	return (
		<ChartScaleContext.Provider value={scaled ?? IDENTITY_SCALE}>
			<div
				style={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					overflow: "visible",
					// Suppress render until dimensions are real
					// visibility: scaled ? "visible" : "hidden",
				}}>
				{children}
			</div>
		</ChartScaleContext.Provider>
	);
};

// ─────────────────────────────────────────────────────────────────────────────
// TooSmallFallback (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

export const TooSmallFallback: React.FC<{ title?: string }> = ({ title }) => (
	<div
		style={{
			width: "100%",
			height: "100%",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: "#F8FAFC",
			border: "1px solid #D6DFED",
			borderRadius: 4,
			padding: 8,
			boxSizing: "border-box",
		}}>
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginBottom: 4 }}>
			<circle cx="10" cy="10" r="9" stroke="#D6DFED" strokeWidth="1.5" />
			<path d="M10 6v4M10 14h.01" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
		</svg>
		<span style={{ color: "#9CA3AF", fontSize: 10, textAlign: "center", lineHeight: "1.4" }}>{title ? `"${title}"` : "Chart"} too small to render</span>
	</div>
);
